import {
  useEffect,
  useRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import cx from "classnames";
import { UseComboboxReturnValue as UseDownshiftComboboxReturnValue } from "downshift";

import { useCombobox } from "./Combobox";
import { ADD_NEW } from "./ComboboxUtils";

import { Icon } from "../Icon";
import { Flex, FlexProps } from "../Flex";

import styles from "./Combobox.module.scss";
import listboxStyles from "../Listbox/Listbox.module.scss";
import listViewStyles from "../ListView/ListView.module.scss";
import checkboxStyles from "../Checkbox/Checkbox.module.scss";
import Check from "@servicetitan/hammer-icon/mdi/round/check.svg";
import CheckboxOutline from "@servicetitan/hammer-icon/mdi/round/check_box_outline_blank.svg";
import CheckboxChecked from "@servicetitan/hammer-icon/mdi/round/check_box.svg";
import CheckboxIndeterminate from "@servicetitan/hammer-icon/mdi/round/indeterminate_check_box.svg";
import { supportsPopover } from "../../internal/functions/supportsPopover";
import {
  ComboboxLegacyItem,
  ComboboxLegacyItemAddNew,
} from "../ComboboxLegacy";

/**
 * Shared props for combobox item components
 */
type ComboboxItemSharedProps = ComponentPropsWithoutRef<"li" | "div"> & {
  children?: ReactNode;
  disabled?: boolean;

  alignItems?: FlexProps["alignItems"];
  justifyItems?: FlexProps["justifyItems"];
  alignContent?: FlexProps["alignContent"];
  justifyContent?: FlexProps["justifyContent"];
  placeItems?: FlexProps["placeItems"];
  placeContent?: FlexProps["placeContent"];
  gap?: FlexProps["gap"];
  rowGap?: FlexProps["rowGap"];
  columnGap?: FlexProps["columnGap"];
};

/**
 * Props for the ComboboxItem component
 * @template Item - The type of items in the combobox
 */
export type ComboboxItemProps<Item> = ComboboxItemSharedProps & {
  /** The item to render */
  item: Item;
  /** The index of the item in the list */
  index: number;

  hideCheckbox?: boolean;

  forceCloseOnSelect?: boolean;
  forceClearInputValueOnSelect?: boolean;
};

/**
 * Props for the ComboboxItemAddNew component
 * @template Item - The type of items in the combobox
 */
export type ComboboxItemAddNewProps<Item> = ComboboxItemSharedProps & {
  /** Callback when the "Add New" item is selected */
  onSelection?: (
    inputValue: UseDownshiftComboboxReturnValue<Item>["inputValue"],
  ) => void;
};

const ComboboxItemElement = function <Item = any>({
  children,
  disabled: disabledProp,
  item,
  index: indexProp,
  hideCheckbox,
  forceCloseOnSelect,
  forceClearInputValueOnSelect,
  alignItems,
  justifyItems,
  alignContent,
  justifyContent,
  placeItems,
  placeContent,
  gap,
  rowGap,
  columnGap,
  ...rest
}: ComboboxItemProps<Item>) {
  const {
    itemToKey,
    getItemProps,
    multiple,
    selectedItem,
    selectedItems,
    disabledItems,
    setDisabledItems,
    setForceCloseOnSelectItems,
    setForceClearInputValueOnSelectItems,
    highlightedIndex,
    items,
    groupBy,
  } = useCombobox<Item>();

  // We can't trust the index prop when using groups
  const index = groupBy ? items.indexOf(item) : indexProp;

  const disabled =
    disabledProp ||
    (item && item instanceof Object && "disabled" in item && item.disabled);

  useEffect(() => {
    if (disabledProp && !disabledItems.includes(item)) {
      setDisabledItems([...disabledItems, item]);
    } else if (disabledItems.includes(item)) {
      setDisabledItems(disabledItems.filter((prevItem) => prevItem !== item));
    }

    return () => {
      if (disabledItems.includes(item)) {
        setDisabledItems(disabledItems.filter((prevItem) => prevItem !== item));
      }
    };
  }, [item, disabledProp, setDisabledItems]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (forceCloseOnSelect) {
      setForceCloseOnSelectItems((forceCloseOnSelectItems) => {
        if (forceCloseOnSelectItems.includes(item)) {
          return forceCloseOnSelectItems;
        } else {
          return [...forceCloseOnSelectItems, item];
        }
      });
    }

    return () => {
      setForceCloseOnSelectItems((forceCloseOnSelectItems) => {
        if (forceCloseOnSelectItems.includes(item)) {
          return forceCloseOnSelectItems.filter(
            (prevItem) => prevItem !== item,
          );
        } else {
          return forceCloseOnSelectItems;
        }
      });
    };
  }, [item, forceCloseOnSelect, setForceCloseOnSelectItems]);

  useEffect(() => {
    if (forceClearInputValueOnSelect) {
      setForceClearInputValueOnSelectItems(
        (forceClearInputValueOnSelectItems) => {
          if (forceClearInputValueOnSelectItems.includes(item)) {
            return forceClearInputValueOnSelectItems;
          } else {
            return [...forceClearInputValueOnSelectItems, item];
          }
        },
      );
    }

    return () => {
      setForceClearInputValueOnSelectItems(
        (forceClearInputValueOnSelectItems) => {
          if (forceClearInputValueOnSelectItems.includes(item)) {
            return forceClearInputValueOnSelectItems.filter(
              (prevItem) => prevItem !== item,
            );
          } else {
            return forceClearInputValueOnSelectItems;
          }
        },
      );
    };
  }, [
    item,
    forceClearInputValueOnSelect,
    setForceClearInputValueOnSelectItems,
  ]);

  if (multiple) {
    const checked = selectedItems.some(
      (selectedItem) => itemToKey(selectedItem) === itemToKey(item),
    );

    return (
      <div
        data-anv="combobox-item"
        {...getItemProps({
          item,
          index,
          "aria-selected": highlightedIndex === index,
          ...(rest as ComponentPropsWithoutRef<"div">),
        })}
        className={cx(
          styles["listview-option"],
          listViewStyles["listview-option"],
        )}
      >
        {item !== ADD_NEW && !hideCheckbox ? (
          <div className={cx(styles["cell"], listViewStyles["cell"])}>
            <div className={checkboxStyles["wrapper"]}>
              <div className={checkboxStyles["checkbox"]}>
                <input
                  type="checkbox"
                  checked={checked}
                  aria-checked={checked}
                  aria-label="Select"
                  readOnly
                  tabIndex={-1}
                  {...(disabled ? { disabled: true } : {})}
                />

                <span className={checkboxStyles["icon-wrapper"]} aria-hidden>
                  <Icon
                    size="large"
                    svg={CheckboxIndeterminate}
                    className={checkboxStyles["icon-indeterminate"]}
                  />

                  <Icon
                    size="large"
                    svg={CheckboxChecked}
                    className={checkboxStyles["icon-checked"]}
                  />

                  <Icon
                    size="large"
                    svg={CheckboxOutline}
                    className={checkboxStyles["icon-unchecked"]}
                  />
                </span>

                <span aria-hidden></span>
              </div>
            </div>
          </div>
        ) : null}

        <Flex
          grow={1}
          shrink={1}
          alignItems={alignItems}
          justifyItems={justifyItems}
          alignContent={alignContent}
          justifyContent={justifyContent}
          placeItems={placeItems}
          placeContent={placeContent}
          gap={gap}
          rowGap={rowGap}
          columnGap={columnGap}
        >
          {children}
        </Flex>
      </div>
    );
  } else {
    return (
      <li
        {...getItemProps({
          item,
          index,
          "aria-selected": highlightedIndex === index,
          ...(rest as ComponentPropsWithoutRef<"li">),
        })}
        data-anv="combobox-item"
        data-checked={itemToKey(selectedItem) === itemToKey(item)}
        className={styles["item"]}
        {...(disabled ? { "aria-disabled": true } : {})}
      >
        <Flex
          className={listboxStyles["children"]}
          grow={1}
          shrink={1}
          alignItems={alignItems}
          justifyItems={justifyItems}
          alignContent={alignContent}
          justifyContent={justifyContent}
          placeItems={placeItems}
          placeContent={placeContent}
          gap={gap}
          rowGap={rowGap}
          columnGap={columnGap}
        >
          {children}
        </Flex>
        <Icon
          aria-hidden
          svg={Check}
          inherit
          className={styles["Icon"]}
          style={{
            opacity: itemToKey(selectedItem) === itemToKey(item) ? 1 : 0,
          }}
        />
      </li>
    );
  }
};

/**
 * ComboboxItem component for rendering individual items in a combobox list
 *
 * Features:
 * - Supports both single and multiple selection modes
 * - Handles disabled state for individual items
 * - Provides checkbox UI for multiple selection
 * - Supports custom layout with Flex props
 * - Integrates with combobox state management
 *
 * @template Item - The type of items in the combobox
 * @param props - The component props
 * @returns A rendered combobox item
 *
 * @example
 * <ComboboxItem item={user} index={0}>
 *   <Text>{user.name}</Text>
 *   <Text subdued>{user.email}</Text>
 * </ComboboxItem>
 */
export const ComboboxItem = function <Item = any>(
  props: ComboboxItemProps<Item>,
) {
  if (!supportsPopover()) return <ComboboxLegacyItem<Item> {...props} />;
  return <ComboboxItemElement {...props} />;
};

const ComboboxItemAddNewElement = function <Item = any>({
  onSelection,
  ...props
}: ComboboxItemAddNewProps<Item>) {
  const { items, setHasAddNew, hasExactMatch, setOnSelectItemAddNew } =
    useCombobox<Item>();

  useEffect(() => {
    setHasAddNew?.(true);

    return () => {
      setHasAddNew?.(false);
    };
  }, []);

  useEffect(() => {
    setOnSelectItemAddNew?.(() => onSelection);

    return () => {
      setOnSelectItemAddNew?.(undefined);
    };
  }, [setOnSelectItemAddNew, onSelection]);

  // hide the "Add New" item on our initial render so it doesn't flicker
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current === true) {
      firstRenderRef.current = false;
    }
  }, []);

  return !hasExactMatch && !firstRenderRef.current ? (
    <ComboboxItem<Item>
      {...props}
      item={ADD_NEW as Item}
      index={items.length}
    />
  ) : null;
};

/**
 * ComboboxItemAddNew component for rendering an "Add New" option in a combobox
 *
 * Features:
 * - Allows users to add new items that don't exist in the list
 * - Integrates with combobox state management
 * - Supports custom layout with Flex props
 * - Handles selection callback for new item creation
 *
 * @template Item - The type of items in the combobox
 * @param props - The component props
 * @returns A rendered "Add New" item or null
 *
 * @example
 * <ComboboxItemAddNew onSelection={(value) => createNewItem(value)}>
 *   <Text>Add "{inputValue}" as new item</Text>
 * </ComboboxItemAddNew>
 */
export const ComboboxItemAddNew = function <Item = any>(
  props: ComboboxItemAddNewProps<Item>,
) {
  if (!supportsPopover()) return <ComboboxLegacyItemAddNew<Item> {...props} />;
  return <ComboboxItemAddNewElement {...props} />;
};
